import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {DecimalPipe} from "@angular/common";
import {CcBaseComponent} from "../../../../../crystal-elements/cc-base.component";
import {FormFieldGroupConfig} from "../../../../../model/api/form-field-group-config";
import {FormFieldConfig} from "../../../../../model/api/form-field-config";
import {BatchUpdateRequest} from "../../../../../model/api/batch-update-request";
import {CcFormDialogComponent} from "../../../../../crystal-elements/form-dialog/cc-form-dialog.component";
import {Order} from "../../../../../model/clearing/order";
import {
  OrderAmendmentDataLoadingService
} from "../../../../../services/data-amendment/order-amendment-data-loading.service";
import {FieldsService} from "../../../../../services/common/fields-service";
import TYPE = FormFieldConfig.TYPE;
import {BatchUtils} from "../../../../../utils/batch-utils";

@Component({
  selector: 'app-batch-update-order-amendment-form',
  templateUrl: './batch-update-order-amendment-form.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
    DecimalPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchUpdateOrderAmendmentFormComponent extends CcBaseComponent {


  formLoading: boolean = false;

  formSubmitted: boolean = true;

  formConfig: FormFieldGroupConfig[] = [];

  dialogFormTitle: string = '';

  @Input()
  formRecord: Order;
  @Input()
  transPartKey: string;
  @Input()
  selectedRecords: Order[] = [];
  @Input()
  dialogFormVisible: boolean = false;
  @Output()
  dialogFormVisibleChange = new EventEmitter<boolean>();

  @Output()
  onSavedForm = new EventEmitter<BatchUpdateRequest<Order>>();

  @ViewChild('batchUpdateFormDialog')
  ccFormDialog: CcFormDialogComponent;

  fieldsToUpdate: string[] = [];

  constructor(cdref: ChangeDetectorRef,
              messageService: MessageService,
              translateService: TranslateService,
              private dataLoadingService: OrderAmendmentDataLoadingService,
              private fieldsService: FieldsService) {
    super(cdref, messageService, translateService);
  }

  protected initConfigs() {
    super.initConfigs();
    this.initFormConfigs();
    this.dialogFormTitle = this.getTranslation('page.' + this.transPartKey + '.table.batch_update_dialogue');
  }


  protected initFormConfigs() {

    const fields = [];
    if (this.isAdmin()) {
      fields.push({
          field: 'exchangeCode',
          column: 2,
          options: () => this.dataLoadingService.exchanges,
          icon: (id: string) => {
            if (this.dataLoadingService.exchanges) {
              const exchField = this.dataLoadingService.exchanges.find(exchange => exchange.id === id);
              if (exchField && exchField.value)
                return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
            }
            return 'fas fa-globe-europe';
          },
          type: TYPE.DROPDOWN,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('exchangeCode')
        },
        {
          field: 'side',
          column: 2,
          options: () => this.dataLoadingService.sides,
          type: TYPE.DROPDOWN,
          labelCheckbox: true,
          labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
          disabled: (record: any) => !this.fieldsToUpdate.includes('side')
        },);
    } else {
      fields.push({
          field: 'exchangeCode',
          column: 2,
          type: TYPE.NONE,
          icon: (id, formRecord) => {
            return '';
          },
          iconValue: id => this.formRecord.exchangeCode
        },
        {
          field: 'side',
          column: 2,
          type: TYPE.NONE,
          icon: (id, formRecord) => {
            return '';
          },
          iconValue: id => this.formRecord.side
        },);
    }
    fields.push(
      {
        field: 'volume',
        column: 2,
        type: TYPE.INTEGER,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('volume')
      },
      {
        field: 'maturity',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('maturity')
      },
      {
        field: 'contractCode',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('contractCode')
      },
      {
        field: 'price',
        column: 2,
        type: TYPE.DECIMAL,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('price')
      },
      {
        field: 'avgPrice',
        column: 2,
        type: TYPE.DECIMAL,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('avgPrice')
      },
      {
        field: 'reference',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('reference'),
        input: (event, formRecord, inputField) => {
          var start = inputField.selectionStart;
          var end = inputField.selectionEnd;
          inputField.value = inputField.value.toUpperCase();
          formRecord.reference = inputField.value;
          inputField.setSelectionRange(start, end);
        },
      },
      {
        field: 'avgPriceGroupId',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('avgPriceGroupId'),
        input: (event, formRecord, inputField) => {
          var start = inputField.selectionStart;
          var end = inputField.selectionEnd;
          inputField.value = inputField.value.toUpperCase();
          formRecord.avgPriceGroupId = inputField.value;
          inputField.setSelectionRange(start, end);
        },
      },
      {
        field: 'customerName',
        column: 2,
        type: TYPE.AUTOCOMPLETE,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('customerName'),
        options: (record, query) => {
          this.dataLoadingService.filterCustomers(record);
          return this.fieldsService.getDistinctOpts(this.dataLoadingService.displayedCustomers)
        },
        changed: (event, record) => {
          queueMicrotask(() => {
            this.dataLoadingService.forceCustomerValues(record, event.text, event.id);
            this.reloadTableValueMap();
          });
        }
      },
      {
        field: 'accountName',
        column: 2,
        type: TYPE.AUTOCOMPLETE,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('accountName'),
        options: (record, query) => {
          return this.fieldsService.getDistinctOpts(this.dataLoadingService.filterAccounts(record, query));
        },
        changed: (event, record) => {
          queueMicrotask(() => this.dataLoadingService.forceAccountValues(record, event.text));
        }
      },
      {
        field: 'accountType',
        column: 2,
        type: TYPE.AUTOCOMPLETE,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('accountType'),
        options: (record) => {
          this.dataLoadingService.filterAccountTypes(record)
          return this.fieldsService.getDistinctOpts(this.dataLoadingService.displayedAccountTypes)
        },
      },
      {
        field: 'giveUpBrokerCode',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('giveUpBrokerCode')
      },
      {
        field: 'giveUpMessage',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('giveUpMessage'),
        input: (event, formRecord, inputField) => {
          var start = inputField.selectionStart;
          var end = inputField.selectionEnd;
          inputField.value = inputField.value.toUpperCase();
          formRecord.giveUpMessage = inputField.value;
          inputField.setSelectionRange(start, end);
        },
      },
      {
        field: 'accountReference1',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('accountReference1'),
        input: (event, formRecord, inputField) => {
          var start = inputField.selectionStart;
          var end = inputField.selectionEnd;
          inputField.value = inputField.value.toUpperCase();
          formRecord.accountReference1 = inputField.value;
          inputField.setSelectionRange(start, end);
        },
      },
      {
        field: 'clearingBroker',
        column: 2,
        type: TYPE.AUTOCOMPLETE,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('clearingBroker'),
        options: () => {
          return this.fieldsService.getDistinctOpts(this.dataLoadingService.moneyOwners)
        },
        changed: (event, record) => {
          record.moneyOwner = event.text;
        }
      },
      {
        field: 'brokerageFee',
        column: 2,
        type: TYPE.DECIMAL,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('brokerageFee')
      },
      {
        field: 'brokerageCurrency',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('brokerageCurrency')
      },
      {
        field: 'brokerageRateAmount',
        column: 2,
        type: TYPE.DECIMAL,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('brokerageRateAmount')
      },
      {
        field: 'submittingTrader',
        column: 2,
        type: TYPE.AUTOCOMPLETE,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('submittingTrader'),
        options: (record) => this.fieldsService.getDistinctOpts(this.dataLoadingService.execTraders),
        changed: (event, record) => {
          queueMicrotask(() => this.dataLoadingService.setBrokerageRate(record));
        }
      },
      {
        field: 'trader',
        column: 2,
        type: TYPE.AUTOCOMPLETE,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('trader'),
        options: (record) => this.fieldsService.getDistinctOpts(this.dataLoadingService.filterTraders(record)),
      },
      {
        field: 'tradeType',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('tradeType')
      },
      {
        field: 'strategy',
        column: 2,
        options: () => this.dataLoadingService.strategies,
        type: TYPE.DROPDOWN,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('strategy')
      },
      {
        field: 'contractUnit',
        column: 2,
        type: TYPE.TEXT,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('contractUnit')
      },
      {
        field: 'callPut',
        column: 2,
        options: () => this.dataLoadingService.callPuts,
        type: TYPE.DROPDOWN,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('callPut')
      },
      {
        field: 'strike',
        column: 2,
        type: TYPE.DECIMAL,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('strike')
      },
      {
        field: 'overrideAvgPrice',
        column: 2,
        type: TYPE.DECIMAL,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('overrideAvgPrice')
      },
      {
        field: 'avgPriceResidual',
        column: 2,
        type: TYPE.DECIMAL,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('avgPriceResidual')
      },
      {
        field: 'avgRndPrice',
        column: 2,
        type: TYPE.DECIMAL,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('avgRndPrice')
      },
      {
        field: 'state',
        column: 2,
        options: () => this.dataLoadingService.orderStates,
        type: TYPE.DROPDOWN,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('state')
      },
      {
        field: 'clearingDate',
        column: 2,
        type: TYPE.DATE,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('clearingDate')
      },
      {
        field: 'businessDate',
        column: 2,
        type: TYPE.DATE,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('businessDate')
      },
      {
        field: 'executionDate',
        column: 2,
        type: TYPE.DATETIME,
        labelCheckbox: true,
        labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
        disabled: (record: any) => !this.fieldsToUpdate.includes('executionDate')
      });
    this.formConfig = [
      {
        group: '',
        fields: fields
      }
    ];
  }

  closeDialogForm(): void {
    this.dialogFormVisible = false;
    this.dialogFormVisibleChange.emit(false);
    this.markForCheck();
  }

  save() {
    // this.formLoading = true;
    const batchUpdateRequest = new BatchUpdateRequest<Order>();
    batchUpdateRequest.recordToApply = {...this.formRecord}
    batchUpdateRequest.recordIds = this.selectedRecords.map(value => value.id);
    batchUpdateRequest.fieldToUpdate = [...this.fieldsToUpdate];


    this.onSavedForm.emit(batchUpdateRequest);
    // this.securityUserService.saveBatch(batchUpdateRequest).subscribe(validation => {
    //   if (validation.success) {
    //     this.fieldsToUpdate.splice(0)
    //     this.onSavedForm.emit(this.formRecord);
    //     this.closeDialogForm();
    //   } else {
    //     this.errorMessage(validation.message);
    //   }
    //   this.formLoading = false;
    // });

  }


  dialogFormVisibleChanged($event: boolean) {
    this.dialogFormVisibleChange.emit($event);
    this.fieldsToUpdate.splice(0);
  }

  validate(): string {
    return this.fieldsToUpdate.length == 0 ? 'fail' : '';
  }

  private reloadTableValueMap() {
    this.ccFormDialog.reloadFieldValueMap();
  }
}
